import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaBlock from "@/components/CtaBlock";
import { Tag, ArrowRight, Ticket } from "lucide-react";

export default function Specials() {
  const { data: promos = [] } = useQuery({ queryKey: ["specials"], queryFn: async () => (await api.get("/promotions")).data });
  return (
    <>
      <Seo title="HVAC Specials & Coupons | Aquila Air & Heating" description="Current HVAC specials, coupons, and promotions from Aquila Air & Heating. Save on AC tune-ups, new system installations, and more across the Space Coast." path="/specials/" />
      <section className="bg-navy text-white py-12 md:py-16 dot-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Specials" }]} /></div>
          <p className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch"><Tag className="h-4 w-4" /> Limited-Time Savings</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">Current Specials & Offers</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-200">Save on the HVAC service your home needs. Mention the code when you call or request service.</p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {promos.length === 0 ? (
            <p className="text-center text-slate-500">No active specials right now — check back soon or call for current offers!</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {promos.map((p) => (
                <div key={p.id} data-testid={`special-${p.id}`} className="relative bg-white border-2 border-dashed border-launch rounded-xl p-6 flex flex-col clip-launch">
                  <Ticket className="h-9 w-9 text-launch" />
                  <h2 className="mt-3 text-xl font-bold text-navy">{p.title}</h2>
                  <p className="mt-2 text-sm text-slate-600 flex-1">{p.description}</p>
                  {p.code && <div className="mt-4 inline-flex items-center gap-2 bg-slate-100 rounded-md px-3 py-2 text-sm"><span className="text-slate-500">Code:</span><span className="font-bold text-navy tracking-wider">{p.code}</span></div>}
                  {p.expires_at && <p className="mt-2 text-xs text-slate-400">Expires {p.expires_at}</p>}
                  <Link to="/contact-us" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-launch hover:gap-2 transition-all">Claim this offer <ArrowRight className="h-4 w-4" /></Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="pb-14 md:pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 prose-launch">
          <h2 className="text-2xl font-bold text-navy">Save on HVAC Service Across the Space Coast</h2>
          <p className="mt-3 text-slate-700 leading-relaxed">
            Aquila Air &amp; Heating helps Space Coast homeowners save on the heating and air conditioning
            service they need with current specials, coupons, and seasonal promotions. From air conditioning
            tune-ups and maintenance memberships to discounts on new high-efficiency system installations and
            indoor air quality upgrades, our offers are designed to make expert HVAC service more affordable
            across Cocoa, Rockledge, Merritt Island, Titusville, Melbourne, and the surrounding Brevard County area.
          </p>
          <p className="mt-4 text-slate-700 leading-relaxed">
            Because Florida runs its air conditioners harder than almost anywhere in the country, staying ahead
            of repairs and keeping your system tuned is one of the smartest ways to protect your comfort and your
            wallet. Our seasonal specials often include savings on precision tune-ups that catch small problems
            before they become expensive breakdowns, promotional pricing on new system installations with flexible
            financing, and member-exclusive discounts through our maintenance club.
          </p>
          <p className="mt-4 text-slate-700 leading-relaxed">
            To claim a special, simply mention the promo code when you call or request service online and our team
            will apply the savings to your visit. Offers are updated regularly and may be limited-time, so it is
            worth checking back or calling to confirm the current promotions available for your home. Every job is
            backed by licensed, insured technicians, upfront and honest pricing you approve before any work begins,
            and our workmanship guarantee. Contact Aquila Air &amp; Heating today to learn about this month's HVAC
            specials and how much you can save on dependable comfort for your Space Coast home.
          </p>
        </div>
      </section>
      <CtaBlock />
    </>
  );
}
